import type {
	BaseSuggestionHandlerConfig,
	GetLocationFn
} from './shared/types';
import type {
	Suggestion,
	SuggestionCondoResult
} from '~/components/SearchSuggest/types';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';
import type { Ref } from 'vue';

import {
	createHandlerMapBuilder,
	createStrategyHandler
} from './shared/baseStrategyHandler';
import {
	closeIfNeeded,
	createLocationEvent,
	fetchAndUpdateLocation,
	getDisplayText,
	getTextFromParams,
	needsLocationFetch,
	updateSearchTerm
} from './shared/utils';

/**
 * Configuration for suggestion handler
 * Extends base config with second-field specific requirements
 */
interface SuggestionHandlerConfig extends BaseSuggestionHandlerConfig {
	city?: string;
	state?: string;
}

/**
 * Emitter functions for different selection types
 */
interface SuggestionEmitters {
	emitHistory: (suggestion: Suggestion, isEmptyState: boolean) => void;
	emitCode: (searchParams: Suggestion['searchParams']) => void;
	emitNeighborhood: (event: SelectAddressEvent) => void;
	emitStreet: (event: SelectAddressEvent) => void;
	emitPoi: (event: SelectAddressEvent) => void;
	emitCondo: (value: SuggestionCondoResult) => void;
}

/**
 * Core composable for handling suggestion selection with Strategy Pattern
 *
 * Refactored to use shared utilities following DRY, KISS, and SOLID principles:
 * - DRY: Shared logic extracted to utilities
 * - KISS: Each handler has a single, clear purpose
 * - Single Responsibility: Each handler focuses on one suggestion type
 * - Open/Closed: Easy to add new suggestion types without modifying existing code
 * - Dependency Inversion: Depends on abstractions (handlers) not concrete implementations
 */
export function useSuggestionHandler(
	emitters: SuggestionEmitters,
	getLocation: GetLocationFn,
	shouldShowEmptyState: Ref<boolean>
) {
	/**
	 * Get display text for a suggestion
	 * Uses shared utility with proper separator for location types
	 */
	function getSelectText(suggestion: Suggestion): string {
		const { searchType } = suggestion;
		const isLocationType = ['region', 'macroregion', 'city'].includes(
			searchType
		);
		return getDisplayText(suggestion, {
			separator: isLocationType ? ' - ' : ', '
		});
	}

	/**
	 * Handler for history items
	 * Uses shared utilities for DRY compliance
	 */
	const handleHistory = async (
		suggestion: Suggestion,
		config: SuggestionHandlerConfig
	) => {
		closeIfNeeded(config);

		const isPoiOrStreet = ['point_of_interest', 'street'].includes(
			suggestion.searchType
		);

		if (isPoiOrStreet) {
			const { selectedMacroregion } = useTwoFieldSearchSuggestState();

			if (!selectedMacroregion.value && suggestion.macroregionUsed) {
				selectedMacroregion.value = suggestion.macroregionUsed;
			}
		}

		const text = getTextFromParams(suggestion, getSelectText);

		if (text) {
			config.searchTerm.value = text;
		}

		// For street and POI, get location if missing
		if (needsLocationFetch(suggestion)) {
			const updatedSuggestion = await fetchAndUpdateLocation(
				suggestion,
				text,
				getLocation
			);
			config.onUpdateResearch(updatedSuggestion);
			emitters.emitHistory(updatedSuggestion, shouldShowEmptyState.value);
		} else {
			config.onUpdateResearch(suggestion);
			emitters.emitHistory(suggestion, shouldShowEmptyState.value);
		}
	};

	/**
	 * Handler for code suggestions
	 */
	const handleCode = (
		suggestion: Suggestion,
		config: SuggestionHandlerConfig
	) => {
		updateSearchTerm(suggestion, config.searchTerm);
		config.onUpdateResearch(suggestion);
		emitters.emitCode(suggestion.searchParams);
		closeIfNeeded(config);
	};

	/**
	 * Handler for region/neighborhood suggestions
	 */
	const handleRegion = (
		suggestion: Suggestion,
		config: SuggestionHandlerConfig
	) => {
		const text = getSelectText(suggestion);
		config.searchTerm.value = text;
		config.onUpdateResearch(suggestion);

		const { searchParams, searchType } = suggestion;

		const states =
			searchParams.states || (config.state ? [config.state] : undefined);

		const event = createLocationEvent(suggestion, undefined, {
			macroregions: searchParams.macroregions || [],
			cities: searchParams.cities || (config.city ? [config.city] : []),
			states,
			regions: searchParams.regions,
			searchType
		});

		emitters.emitNeighborhood(event);
		config.onClose();
	};

	/**
	 * Handler for street suggestions
	 */
	const handleStreet = async (
		suggestion: Suggestion,
		config: SuggestionHandlerConfig
	) => {
		config.onClose(); // Always close for streets

		const { selectedMacroregion } = useTwoFieldSearchSuggestState();

		if (!selectedMacroregion.value && suggestion.macroregionUsed) {
			selectedMacroregion.value = suggestion.macroregionUsed;
		}

		const text = updateSearchTerm(suggestion, config.searchTerm);
		const updatedSuggestion = await fetchAndUpdateLocation(
			suggestion,
			text,
			getLocation
		);

		config.onUpdateResearch(updatedSuggestion);

		const { lat, lng } = updatedSuggestion.searchParams;
		const event = createLocationEvent(suggestion, text, {
			lat: lat ?? undefined,
			lng: lng ?? undefined,
			macroregions: [],
			cities: [],
			regions: [],
			states: suggestion.searchParams.states || ['sp']
		});

		emitters.emitStreet(event);
	};

	/**
	 * Handler for point of interest suggestions
	 */
	const handlePoi = async (
		suggestion: Suggestion,
		config: SuggestionHandlerConfig
	) => {
		config.onClose(); // Always close for POI

		const { selectedMacroregion } = useTwoFieldSearchSuggestState();

		if (!selectedMacroregion.value && suggestion.macroregionUsed) {
			selectedMacroregion.value = suggestion.macroregionUsed;
		}

		const text = updateSearchTerm(suggestion, config.searchTerm);
		const updatedSuggestion = await fetchAndUpdateLocation(
			suggestion,
			text,
			getLocation
		);

		config.onUpdateResearch(updatedSuggestion);

		const { lat, lng } = updatedSuggestion.searchParams;
		const event = createLocationEvent(suggestion, text, {
			lat: lat ?? undefined,
			lng: lng ?? undefined,
			macroregions: [],
			cities: [],
			regions: []
		});

		emitters.emitPoi(event);
	};

	/**
	 * Handler for condo suggestions
	 */
	const handleCondo = (
		suggestion: Suggestion,
		config: SuggestionHandlerConfig
	) => {
		updateSearchTerm(suggestion, config.searchTerm);
		config.onUpdateResearch(suggestion);

		emitters.emitCondo({
			...suggestion.searchParams,
			condoSlug: suggestion.searchParams.condoSlug,
			condoName: suggestion.result.title,
			textSearched: config.searchTerm.value
		} as SuggestionCondoResult);

		config.onClose(); // Always close for condos
	};

	/**
	 * Build strategy map using handler builder pattern
	 * Makes it clear and maintainable to see which types use which handlers
	 */
	const handlerMap = createHandlerMapBuilder<SuggestionHandlerConfig>()
		.register('research', handleHistory)
		.register('code', handleCode)
		.register('region', handleRegion)
		.register('street', handleStreet)
		.register('point_of_interest', handlePoi)
		.register('condo', handleCondo)
		.build();

	/**
	 * Create the main handler using shared strategy pattern implementation
	 * This eliminates duplication of the delegation logic
	 */
	const { handleSuggestion } = createStrategyHandler(handlerMap);

	return {
		handleSuggestion,
		getSelectText
	};
}
